import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import HeaderV1 from "../NewDashboard/HeaderV1";
import SideBarV1 from "../NewDashboard/SideBarV1";
import DashboardBackground from "../../components/ReUsableComponents/DashboardBackground";
import FailureLogo from '../../assets/Images/FailureCircle.svg'
import SuccessLogo from '../../assets/Images/SuccessCircle.svg'
import AuthButton from "../LogInPage/LoginButton";
import { BiArrowBack, BiGridAlt } from "react-icons/bi";
import PlanDetailsCard from "./PlanDetailsCard";
import { formatDate, handleTokenChanges } from "../../utils";
import Lottie from "react-lottie-player";
import verifyPaymentLoader from "../../assets/AnimationFiles/PaymentGatewayAnimation.json";
import { useLazyVerifyPaymentQuery } from "../../redux/API/Payments/paymentsapi";
import { Plan } from "./Plans";
import { getItem } from "../../utils/token";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/Store/store";

export type PaymentData = {
  planDetails: Plan;
  paymentMode: string;
  actualAmount: number;
  discount: number;
  tax: number;
  paidAmount: number;
  token: string;
  paymentType: string;
  transactionId: string;
  dateOfPurchase: string;
}

const TransactionStatus = () => {

  const { status,SecureTxnID } = useParams();
  const [verifyPayment,{isLoading}] = useLazyVerifyPaymentQuery();
  const [userSelectedPlan,setUserSelectedPlan] = React.useState<PaymentData | null>(null);
  const [loaderDelayDone, setLoaderDelayDone] = React.useState(false);

  const {startedTestId} = useSelector((state:RootState) => state.root.startFullTest);

  const isFirstTest = getItem('userRole') === 'STUDENT' && startedTestId.isFirstTest;

  const handleTransactionStatus = async() => {
    try{
      const response = await verifyPayment({data:SecureTxnID}).unwrap();
      if(response?.status === 'success'){
        setUserSelectedPlan(response?.data);
      }
    }catch(err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if(status !== 'success' && status !== 'failure') {
      window.location.href = '/login'
    }
    if(SecureTxnID){
      handleTransactionStatus();
    }
  },[status,SecureTxnID]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaderDelayDone(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
   if(userSelectedPlan?.token){
    handleTokenChanges(userSelectedPlan?.token);
   }
  },[userSelectedPlan])

  return (
    <DashboardBackground>
      {!loaderDelayDone || isLoading ? (
        <VerifyPayment />
      ) : (
        <div className={`p-5 flex gap-[1%] w-full ${isFirstTest ? 'flex-col' : 'flex-row'}`}>
          {/* Sidebar */}
          <SideBarV1 isFirstTest={isFirstTest} />

          <section
            className={`flex ${!isFirstTest && 'flex-col'} justify-center items-start gap-[2vh]`}
            style={{ width: isFirstTest ? '100%' : "calc(100vw - 240px)", height: isFirstTest ? '' : "calc(100vh - 40px)" }}
          >
            {!isFirstTest && <HeaderV1/>}

            <div
              className="px-3 py-2 w-full flex flex-grow justify-between items-center self-stretch rounded-[28px] border-[0.5px]  border-satPrepGrey bg-satPrepTransWhite shadow-widget-card backdrop-blur-sevenFive relative"
              style={{ height: "calc(100vh - 12vh - 40px)" }}
            >
              <div className="h-full py-1 w-full overflow-y-auto scroll4">
                  {status === 'success' && <SuccessPage userPlan={userSelectedPlan} />}
                  {status === 'failure' && <FailurePage />}
              </div>
            </div>
          </section>
        </div>
      )}
    </DashboardBackground>
  );
};

export default TransactionStatus;

type SuccessPageProps = {
  userPlan: PaymentData | null;
};

const SuccessPage = ({userPlan} :SuccessPageProps ) => {
    const navigate = useNavigate();

    const handleDashboard = () => {
      if(getItem('userRole') === 'STUDENT'){
        navigate("/dashboard")
      }else{
        navigate('/parentdashboard')
      }
    }

    return (
        <section className="w-full h-full flex flex-col justify-center items-center gap-[72px] font-gully">
            <div className="flex flex-col gap-3 items-center justify-center">
               <img src={SuccessLogo} alt="success-logo" className="w-40 h-40"/>
               <p className="text-satPrepGreen text-[56px] font-bold text-center">Purchase Successful</p>
            </div>

            <div className="flex w-4/5 justify-center gap-[10%]">
                <div className="w-[45%]">
                    <PlanDetailsCard plan={userPlan?.planDetails}/>
                </div>

                <div className="flex flex-col gap-9 w-[45%]" style={{flex: '1 0 0'}}>
                    <div className="flex flex-col gap-3">
                        <p className='text-[26px] font-semibold text-satPrepAccent'>Purchase Summary</p>
                        <div className='flex flex-col gap-[6px]'>
                            <div className='flex justify-between items-center'>
                                <p className='text-xl font-medium'>{userPlan?.planDetails?.planName}</p>
                                <p>{userPlan?.planDetails?.currencySymbol}{userPlan?.planDetails?.price}</p>
                            </div>

                            <div className='flex justify-between items-center'>
                                <p className='text-base font-normal'>Discount</p>
                                <p>{userPlan?.planDetails?.currencySymbol}{userPlan?.discount ?? '-'}</p>
                            </div>

                            <div className='flex justify-between items-center'>
                                <p className='text-base font-normal'>Tax</p>
                                <p>{userPlan?.planDetails?.currencySymbol}{userPlan?.tax ?? '-'}</p>
                            </div>
                        </div>
                        <div className='w-full h-[0.5px] bg-satPrepGrey'></div>
                        <div className='flex justify-between items-center text-satPrepXDarkAccent text-[22px] font-medium'>
                            <p>Total</p>
                            <p>{userPlan?.planDetails?.currencySymbol}{userPlan?.paidAmount ?? '-'}</p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <p className='text-[26px] font-semibold text-satPrepAccent'>Transaction Summary</p>
                        <div className="flex flex-col gap-2">
                           <div className="flex gap-[6px]"><p className="font-medium">Payment Mode:</p> {/*<img src={userPlan?.planDetails?.paymentLogo} alt="payment-type" className="w-5 h-5 -mt-1"/>*/}<p className="font-normal">{userPlan?.paymentMode ?? '-'}</p></div>
                           <p className="font-medium">Transaction ID: <span className="font-normal">{userPlan?.transactionId ?? '-'}</span></p>
                           <p className="font-medium">Purchase Date: <span className="font-normal">{formatDate(userPlan?.dateOfPurchase)}</span></p>
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <AuthButton
                    text="Return to Dashboard"
                    iconPosition="left"
                    icon={<BiGridAlt size={22} />}
                    onClick={handleDashboard}
                    className="w-full"
                />
            </div>
        </section>
    )
}


const FailurePage = () => {
    const navigate = useNavigate();

    const handleDashboard = () => {
      if(getItem('userRole') === 'STUDENT'){
        navigate("/dashboard")
      }else{
        navigate('/parentdashboard')
      }
    }

    return (
        <section className="flex justify-center items-center flex-col w-full h-full">
           <div className="flex flex-col justify-center items-center gap-9 w-2/5">
              <div className="flex flex-col items-center gap-3 font-gully">
                <img src={FailureLogo} alt="failed-logo" className="w-40 h-40"/>
                <p className="text-satPrepRed text-[56px] font-bold text-center">Transaction Failed</p>
                <p className="font-medium text-center">Please try again or reach out to us at <a href="mailto:support@fullmarks.io" className="text-satPrepAccent underline">support@fullmarks.io</a> if the issue persists.</p>
              </div>

              <div className="flex gap-3 w-full">
                <div className="w-fit">
                    <AuthButton
                        text="Return to Dashboard"
                        iconPosition="left"
                        icon={<BiGridAlt size={22} />}
                        onClick={handleDashboard}
                        className="!border !border-[#828285] !bg-satPrepLightGrey !text-satPrepBlack  !px-[22px]"
                    />
                </div>
                <div className="flex-1">
                    <AuthButton
                        text="Retry Purchase"
                        iconPosition="right"
                        icon={
                        <div className="rotate-180">
                            <BiArrowBack size={24} />
                        </div>
                        }
                        onClick={() => navigate("/plans")}
                        className="w-full"
                    />
              </div>
          </div>
            </div>
        </section>
    ) 
}


const VerifyPayment = () => {

  return (
    <DashboardBackground>
      <div className="bg-satPrepTransWhite m-auto rounded-[28px] border-[0.5px] border-satPrepGrey shadow-widget-card backdrop-blur-sevenFive flex justify-center items-center" style={{ height: "calc(100vh - 40px)",width: "calc(100vw - 40px)" }}>
        <Lottie
          loop
          animationData={verifyPaymentLoader}
          play
          style={{ width : 400, height: 400 }}
        />
      </div>
    </DashboardBackground>
  )
}